import withRouter from 'found/withRouter'
import PropTypes from 'prop-types'
import React from 'react'
import root from 'window-or-global'

import { withI18n } from '@thebeansgroup/i18n'

import { ROUTES } from '@src/authorisation/children/router/constants'
import { navigateToResetPasswordPage } from '@src/authorisation/children/router/helpers'
import { asyncRequest } from '@src/authorisation/helpers'
import { isExpressConnectSession } from '@src/helpers/medium'
import { isWebview } from '@src/helpers/webviews'
import Snowplow from '@src/snowplow'

import SSOButton from '@components/sso_button'
import SSODisabledModal from '@components/sso_disabled_modal'

import {
  SSO_TYPE_FACEBOOK,
  SSO_TYPE_GOOGLE,
  SSO_TYPE_APPLE,
  POP_UP_CLOSED_BY_USER
} from './constants'
import {
  getGoogleAuthParams,
  responseHasNotFoundError,
  getFacebookRoute,
  getGoogleRoute,
  isGoogleLoading,
  isFacebookLoading,
  isGoogleResponseValid,
  isFacebookResponseValid,
  canMakeFacebookSSORequest,
  canMakeGoogleSSORequest,
  canMakeAppleSSORequest,
  configureAppleAuth,
  isAppleResponseValid,
  getResponse,
  trackSSOButtonClick,
  setFacebookSignedRequestCookie,
  getRouterProps,
  responseHasClosedError
} from './helpers'
import * as styles from './styles'

export class SSO extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      ssoError: '',
      ssoInProgress: '',
      showSSODisabledModal: false,
      selectedSSOType: ''
    }
    this.bindHandlers()
  }

  bindHandlers() {
    this.onFacebookClick = this.onFacebookClick.bind(this)
    this.handleFacebookAuthenticate = this.handleFacebookAuthenticate.bind(this)
    this.onGoogleClick = this.onGoogleClick.bind(this)
    this.handleGoogleAuthenticate = this.handleGoogleAuthenticate.bind(this)
    this.handleSSORequestSuccess = this.handleSSORequestSuccess.bind(this)
    this.handleSSORequestFailure = this.handleSSORequestFailure.bind(this)
    this.handleGoogleRequestSuccess = this.handleGoogleRequestSuccess.bind(this)
    this.handleFacebookRequestSuccess =
      this.handleFacebookRequestSuccess.bind(this)
    this.handleAppleRequestSuccess = this.handleAppleRequestSuccess.bind(this)
    this.onAppleClick = this.onAppleClick.bind(this)
    this.closeSSODisabledModal = this.closeSSODisabledModal.bind(this)
    this.showSSODisabledModal = this.showSSODisabledModal.bind(this)
    this.onSSODisabledModalClick = this.onSSODisabledModalClick.bind(this)
    this.handleClosedByUser = this.handleClosedByUser.bind(this)
  }

  handleSSORequestSuccess(data, label) {
    const eventTrackingContexts = [
      Snowplow.getButtonContext({
        text: label,
        style: 'standard',
        location: 'signUp'
      })
    ]
    this.props.requestSuccessHandler(data, eventTrackingContexts)
  }

  handleGoogleRequestSuccess(data) {
    this.handleSSORequestSuccess(data, 'Sign up using Google')
  }

  handleFacebookRequestSuccess(data) {
    this.handleSSORequestSuccess(data, 'Sign up using Facebook')
  }

  handleAppleRequestSuccess(data) {
    this.handleSSORequestSuccess(data, 'Sign up using Apple')
  }

  handleSSORequestFailure(data) {
    if (responseHasNotFoundError(data)) {
      this.props.ssoNotFoundHandler(this.state.ssoInProgress)
    } else if (responseHasClosedError(data)) {
      return this.handleClosedByUser()
    } else {
      this.props.requestErrorHandler(data)
    }
  }

  handleClosedByUser() {
    this.setState({ ssoError: POP_UP_CLOSED_BY_USER })

    return Snowplow.trackFormErrorEvent({
      input_id: 'sso',
      errors: POP_UP_CLOSED_BY_USER,
      input: ''
    })
  }

  handleFacebookAuthenticate(response) {
    const { token, type } = this.props
    if (isFacebookResponseValid(response)) {
      this.setState({ ssoInProgress: 'Facebook' })
      setFacebookSignedRequestCookie()
      asyncRequest(
        token,
        ROUTES.SERVER[getFacebookRoute(type)],
        {},
        this.handleFacebookRequestSuccess,
        this.handleSSORequestFailure
      )
    }
  }

  onFacebookClick() {
    trackSSOButtonClick('facebook')
    if (isWebview()) {
      this.showSSODisabledModal(SSO_TYPE_FACEBOOK)
      return
    }
    if (canMakeFacebookSSORequest(this.props)) {
      root.FB.login(this.handleFacebookAuthenticate, { scope: 'email' })
    }
  }

  handleGoogleAuthenticate(response) {
    const { token, type } = this.props

    if (isGoogleResponseValid(response)) {
      this.setState({ ssoInProgress: 'Google' })
      asyncRequest(
        token,
        ROUTES.SERVER[getGoogleRoute(type)],
        { code: response.code },
        this.handleGoogleRequestSuccess,
        this.handleSSORequestFailure,
        'POST',
        false
      )
    }
  }

  onGoogleClick() {
    trackSSOButtonClick('google')
    if (isWebview()) {
      this.showSSODisabledModal(SSO_TYPE_GOOGLE)
      return
    }

    if (canMakeGoogleSSORequest(this.props)) {
      const client = root.google.accounts.oauth2.initCodeClient({
        ...getGoogleAuthParams(),
        callback: this.handleGoogleAuthenticate
      })

      return client.requestCode()
    }
  }

  handleAppleAuthenticate(data) {
    const { token } = this.props
    if (isAppleResponseValid(data)) {
      const response = getResponse(data)
      this.setState({ ssoInProgress: 'Apple' })
      asyncRequest(
        token,
        ROUTES.SERVER.APPLE_CALLBACK,
        response,
        this.handleAppleRequestSuccess,
        this.handleSSORequestFailure
      )
    }
  }

  onAppleClick() {
    trackSSOButtonClick('apple')
    if (isWebview()) {
      this.showSSODisabledModal(SSO_TYPE_APPLE)
      return
    }
    const { type } = this.props
    if (canMakeAppleSSORequest(this.props)) {
      configureAppleAuth(type, ROUTES)
      root.AppleID.auth
        .signIn()
        .then((data) => {
          this.handleAppleAuthenticate(data)
        })
        .catch((error) => {
          this.handleSSORequestFailure(error)
        })
    }
  }

  renderDescription() {
    if (!this.props.description) return

    return <p css={styles.ssoText}>{this.props.description}</p>
  }

  renderFacebookButton() {
    const { circularButtons, facebook, facebookLabel, medium } = this.props
    if (!facebook || isExpressConnectSession(medium)) return null

    return (
      <SSOButton
        type='Facebook'
        label={facebookLabel}
        circular={circularButtons}
        loading={isFacebookLoading(this.state)}
        clickHandler={this.onFacebookClick}
      />
    )
  }

  renderGoogleButton() {
    const { circularButtons, google, googleLabel } = this.props
    if (!google) return

    return (
      <SSOButton
        type='Google'
        label={googleLabel}
        circular={circularButtons}
        loading={isGoogleLoading(this.state)}
        clickHandler={this.onGoogleClick}
      />
    )
  }

  renderAppleButton() {
    if (!process.env.APPLE_SSO_ENABLED) return null

    const { circularButtons, apple, appleLabel } = this.props
    if (!apple) return

    return (
      <SSOButton
        type='Apple'
        label={appleLabel}
        circular={circularButtons}
        clickHandler={this.onAppleClick}
      />
    )
  }

  closeSSODisabledModal() {
    this.setState({ showSSODisabledModal: false })
  }

  showSSODisabledModal(ssoType) {
    this.setState({
      showSSODisabledModal: true,
      selectedSSOType: ssoType
    })
  }

  onSSODisabledModalClick() {
    navigateToResetPasswordPage(getRouterProps(this.props))
  }

  renderSSODisabledModal() {
    if (!isWebview()) return null

    return (
      <SSODisabledModal
        visible={this.state.showSSODisabledModal}
        handleClick={this.onSSODisabledModalClick}
        handleCloseClick={this.closeSSODisabledModal}
        ssoMethod={this.state.selectedSSOType}
      />
    )
  }

  render() {
    return (
      <>
        {this.renderDescription()}
        {this.renderFacebookButton()}
        {this.renderGoogleButton()}
        {this.renderAppleButton()}
        {this.renderSSODisabledModal()}
      </>
    )
  }
}

SSO.defaultProps = {
  description: '',
  google: false,
  facebook: false,
  circularButtons: true
}

SSO.propTypes = {
  t: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['logIn', 'signUp']).isRequired,
  description: PropTypes.string,
  google: PropTypes.bool,
  facebook: PropTypes.bool,
  googleLabel: PropTypes.string.isRequired,
  facebookLabel: PropTypes.string.isRequired,
  circularButtons: PropTypes.bool,
  requestErrorHandler: PropTypes.func.isRequired,
  requestSuccessHandler: PropTypes.func.isRequired,
  ssoNotFoundHandler: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  medium: PropTypes.string.isRequired
}

export default withRouter(withI18n(SSO))
